import { TagTypes } from '@/types/api/tag-types'

import { ordersApi as generatedOrdersApi } from './orders-slice.gen'

// This slice re-exports all trader profile endpoints from orders-slice.gen after first calling `enhanceEndpoints` to provide and invalidate tags for cache management.
// See RTK docs: https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
// See forbidden-imports.d.ts for forbidden imports.

const toKey = ({ orgId, userId }: { orgId: string; userId: string }) =>
  `userId:${userId}|orgId:${orgId}`

/**
 * Trader profile APIs using the `orders-api` service.
 *
 * Note: The schema differs from the legacy trade-desk APIs:
 * - TraderProfile now includes `org_id` and uses `venue_trader_info` (array) instead of `venue_trader_configs` (map)
 * - VenueTraderInfo is a flat structure with `venue_mpid` and `venue_trader_id`; its `custodian_id` and
 *   `custodian_account_at_venue` fields are unused and sent empty until they are removed from the API
 * - getDefaultTraderForOrg returns `{ user_id: string }` rather than the full TraderProfile
 * - All endpoints require `orgId` as a path parameter
 */
const traderProfilesSlice = generatedOrdersApi.enhanceEndpoints({
  addTagTypes: [TagTypes.TraderProfile],
  endpoints: {
    getDefaultTraderForOrg: {
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      providesTags: (result, error, arg) => [
        { type: TagTypes.TraderProfile, id: arg.orgId },
      ],
    },
    postDefaultTraderForOrg: {
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.TraderProfile, id: arg.orgId },
      ],
    },
    deleteDefaultTraderForOrg: {
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.TraderProfile, id: arg.orgId },
      ],
    },
    getTraderProfile: {
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      providesTags: (result, error, arg) => [
        { type: TagTypes.TraderProfile, id: toKey(arg) },
      ],
    },
    createTraderProfile: {
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.TraderProfile, id: toKey(arg) },
      ],
    },
    updateTraderProfile: {
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.TraderProfile, id: toKey(arg) },
      ],
    },
    deleteTraderProfile: {
      // biome-ignore lint/correctness/noUnusedFunctionParameters: Bulk suppression as part of biome migration
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.TraderProfile, id: toKey(arg) },
      ],
    },
  },
})

export const {
  useGetDefaultTraderForOrgQuery,

  useGetTraderProfileQuery,
  useCreateTraderProfileMutation,
  useUpdateTraderProfileMutation,
} = traderProfilesSlice
